import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Truck, Pencil, Trash2, Power } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { placa: "", tipo: "", descricao: "", capacidade: "", ativo: true };

export default function Veiculos() {
  const { user } = useAuth();
  const canDelete = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const [list, setList] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/veiculos").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditId(null); setModal(true); };
  const openEdit = (v) => { setForm({ placa: v.placa, tipo: v.tipo || "", descricao: v.descricao || "", capacidade: v.capacidade || "", ativo: v.ativo }); setEditId(v.id); setModal(true); };

  const save = async () => {
    if (!form.placa.trim()) return toast.error("Informe a placa");
    setSaving(true);
    try {
      if (editId) await api.put(`/veiculos/${editId}`, form);
      else await api.post("/veiculos", form);
      toast.success(editId ? "Veículo atualizado" : "Veículo cadastrado");
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };
  const toggleAtivo = async (v) => {
    try { await api.put(`/veiculos/${v.id}`, { placa: v.placa, tipo: v.tipo, descricao: v.descricao, capacidade: v.capacidade, ativo: !v.ativo }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const del = async (v) => {
    if (!window.confirm(`Excluir o veículo ${v.placa}?`)) return;
    try { await api.delete(`/veiculos/${v.id}`); toast.success("Veículo excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-5" data-testid="veiculos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Transporte</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Truck className="w-7 h-7 text-primary" /> Veículos</h1>
          <p className="text-sm text-muted-foreground mt-1">Cadastro da frota usada nas cargas de transporte.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-novo-veiculo"><Plus className="w-4 h-4" /> Novo Veículo</Button>
      </div>

      {!list ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : list.length === 0 ? (
        <Card className="bg-card/70 p-8 text-center text-muted-foreground" data-testid="veiculos-vazio">Nenhum veículo cadastrado.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((v) => (
            <Card key={v.id} className={`bg-card/70 p-4 ${v.ativo ? "" : "opacity-60"}`} data-testid={`veiculo-${v.placa}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-xl font-extrabold uppercase font-mono">{v.placa}</div>
                  <div className="text-sm text-muted-foreground">{v.tipo || "—"}{v.capacidade ? ` · ${v.capacidade}` : ""}</div>
                  {v.descricao && <div className="text-xs text-muted-foreground mt-0.5">{v.descricao}</div>}
                </div>
                {!v.ativo && <span className="text-[10px] uppercase font-bold bg-red-500/20 text-red-300 border border-red-500/50 px-1.5 py-0.5 rounded-sm">Inativo</span>}
              </div>
              <div className="flex gap-1.5 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(v)} data-testid={`btn-editar-veiculo-${v.placa}`}><Pencil className="w-3.5 h-3.5" /> Editar</Button>
                <Button size="sm" variant="outline" onClick={() => toggleAtivo(v)} data-testid={`btn-toggle-veiculo-${v.placa}`}><Power className="w-3.5 h-3.5" /> {v.ativo ? "Inativar" : "Ativar"}</Button>
                {canDelete && <Button size="sm" variant="outline" className="text-red-400" onClick={() => del(v)} data-testid={`btn-excluir-veiculo-${v.placa}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-md" data-testid="veiculo-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Editar Veículo" : "Novo Veículo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Placa *</Label><Input value={form.placa} onChange={(e) => set("placa", e.target.value.toUpperCase())} data-testid="veiculo-placa" /></div>
            <div><Label>Tipo</Label><Input value={form.tipo} onChange={(e) => set("tipo", e.target.value)} placeholder="Van, Truck, Moto…" data-testid="veiculo-tipo" /></div>
            <div><Label>Capacidade</Label><Input value={form.capacidade} onChange={(e) => set("capacidade", e.target.value)} placeholder="1.5 t, 8 t…" data-testid="veiculo-capacidade" /></div>
            <div><Label>Descrição</Label><Input value={form.descricao} onChange={(e) => set("descricao", e.target.value)} data-testid="veiculo-descricao" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-salvar-veiculo">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
